import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useNavigate } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import SortableTable from '../components/SortableTable';
import EmailDisplayCell from '../components/providers/EmailDisplayCell';
import ConfigurationStatusBadge from '../components/providers/ConfigurationStatusBadge';
import ProviderRateExpandedRow from '../components/providers/ProviderRateExpandedRow';
import ServiceTypeComparisonView from '../components/providers/ServiceTypeComparisonView';
import { supabase } from '../utils/supabaseClient';
import { fetchServiceTypes } from '../utils/serviceTypeUtils';
import { fetchCompleteProviderData, exportProvidersToExcel } from '../utils/providerExportUtils';

function RateTable() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [activeServiceTypes, setActiveServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentTab, setCurrentTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Table column definitions
  const headCells = [
    {
      id: 'expand',
      label: '',
      align: 'center',
      disableSorting: true,
      sx: { 
        width: '50px',
        position: 'sticky', 
        left: 0, 
        backgroundColor: 'background.paper',
        zIndex: 3,
      }
    },
    {
      id: 'name',
      label: 'Provider Name',
      align: 'left',
      sx: { 
        position: 'sticky', 
        left: 50, 
        backgroundColor: 'background.paper',
        zIndex: 3,
        boxShadow: '2px 0px 3px -1px rgba(0,0,0,0.1)',
        minWidth: '180px'
      }
    },
    { id: 'email', label: 'Email', align: 'left', sx: { minWidth: '200px' } },
    { 
      id: 'configuration_status', 
      label: 'Configuration Status', 
      align: 'center',
      disableSorting: true,
      sx: { minWidth: '180px' }
    },
    { 
      id: 'service_types_configured', 
      label: 'Service Types', 
      align: 'left',
      disableSorting: true,
      sx: { minWidth: '250px' }
    },
    { id: 'net_terms', label: 'Net Terms', align: 'right' },
    { 
      id: 'actions', 
      label: 'Actions', 
      align: 'center', 
      disableSorting: true,
      sx: { 
        position: 'sticky', 
        right: 0, 
        backgroundColor: 'background.paper', 
        zIndex: 2,
        minWidth: '150px'
      } 
    },
  ];

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      
      // Fetch active service types first
      const { data: serviceTypesData, error: serviceTypesError } = await fetchServiceTypes();
      
      if (serviceTypesError) {
        throw serviceTypesError;
      }
      
      setActiveServiceTypes(serviceTypesData || []);
      
      // Fetch providers with their service rate configurations
      const { data: providersData, error: providersError } = await supabase
        .from('providers')
        .select(`
          id,
          name,
          email,
          status,
          net_terms,
          default_hourly_rate,
          default_flat_rate,
          default_refusal_rate,
          provider_service_rates (
            id,
            service_type_id,
            rate_type,
            flat_rate,
            refusal_rate,
            hourly_rate,
            per_board,
            per_location,
            rate_1_unit,
            rate_2_units,
            rate_3_units,
            rate_4_units,
            rate_5_units,
            rate_6_units,
            rate_7_units,
            rate_8_units,
            rate_9_units,
            rate_10_units,
            service_types (
              id,
              name,
              code,
              rate_type
            )
          )
        `)
        .order('name');
        
      if (providersError) {
        throw providersError;
      }
      
      if (providersData) {
        setRows(providersData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setSnackbar({
        open: true,
        message: `Error fetching data: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }

  const handleAddProvider = () => {
    navigate('/provider/add');
  };

  const handleEditProvider = (id) => {
    navigate(`/provider/edit/${id}`);
  };

  const handleViewDetails = (id) => {
    navigate(`/provider/rates/${id}`);
  };

  const handleDeleteClick = (provider) => {
    setProviderToDelete(provider);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const { error } = await supabase
        .from('providers')
        .delete()
        .eq('id', providerToDelete.id);
        
      if (error) {
        throw error;
      }
      
      // Update the local state
      setRows(rows.filter(row => row.id !== providerToDelete.id));
      
      setSnackbar({
        open: true,
        message: `Provider ${providerToDelete.name} deleted successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting provider:', error);
      setSnackbar({
        open: true,
        message: `Error deleting provider: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleExportToExcel = async () => {
    try {
      setExportLoading(true);
      
      // Fetch complete provider data with emails
      const { providers, serviceTypes, error } = await fetchCompleteProviderData();
      
      if (error) {
        throw error;
      }

      if (!providers || providers.length === 0) {
        setSnackbar({
          open: true,
          message: 'No providers to export',
          severity: 'warning'
        });
        return;
      }

      // Export to Excel
      const result = exportProvidersToExcel(providers, serviceTypes);
      
      if (result.success) {
        setSnackbar({
          open: true,
          message: `Successfully exported ${providers.length} providers to ${result.filename}`,
          severity: 'success'
        });
      } else {
        throw new Error(result.error || 'Export failed');
      }
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setSnackbar({
        open: true,
        message: `Export failed: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const toggleExpandRow = (providerId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(providerId)) {
        newSet.delete(providerId);
      } else {
        newSet.add(providerId);
      }
      return newSet;
    });
  };

  // Render each row of the table
  const renderRow = (row) => {
    const isExpanded = expandedRows.has(row.id);
    const configuredServiceTypes = row.provider_service_rates || [];
    
    return (
      <React.Fragment key={row.id}>
        <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
          <TableCell
            sx={{ 
              position: 'sticky', 
              left: 0, 
              backgroundColor: 'background.paper',
              zIndex: 2
            }}
          >
            <IconButton
              size="small"
              onClick={() => toggleExpandRow(row.id)}
              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
            >
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell 
            component="th" 
            scope="row"
            sx={{ 
              position: 'sticky', 
              left: 50, 
              backgroundColor: 'background.paper',
              zIndex: 2,
              boxShadow: '2px 0px 3px -1px rgba(0,0,0,0.1)',
              fontWeight: 'medium',
              cursor: 'pointer'
            }}
            onClick={() => toggleExpandRow(row.id)}
          >
            {row.name}
          </TableCell>
          <TableCell sx={{ wordBreak: 'break-word', maxWidth: 250 }}>
            <EmailDisplayCell providerId={row.id} />
          </TableCell>
          <TableCell align="center">
            <ConfigurationStatusBadge 
              providerServiceRates={configuredServiceTypes}
              activeServiceTypes={activeServiceTypes}
            />
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {configuredServiceTypes.length === 0 ? (
                <Chip label="None" size="small" color="default" />
              ) : (
                configuredServiceTypes.map(config => (
                  <Chip 
                    key={config.id}
                    label={config.service_types?.name || 'Unknown'}
                    size="small"
                    color={config.rate_type === 'FLAT' ? 'primary' : 'secondary'}
                  />
                ))
              )}
            </Box>
          </TableCell>
          <TableCell align="right">{row.net_terms ? `Net ${row.net_terms}` : '-'}</TableCell>
          <TableCell 
            align="center"
            sx={{ 
              position: 'sticky', 
              right: 0, 
              backgroundColor: 'background.paper',
              zIndex: 2 
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
              <Tooltip title="View Details">
                <IconButton onClick={() => handleViewDetails(row.id)} color="info" size="small">
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit provider">
                <IconButton onClick={() => handleEditProvider(row.id)} color="primary" size="small">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete provider">
                <IconButton onClick={() => handleDeleteClick(row)} color="error" size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </TableCell>
        </TableRow>
        
        {/* Expanded row content */}
        {isExpanded && (
          <ProviderRateExpandedRow 
            provider={row}
            activeServiceTypes={activeServiceTypes}
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Provider Rate Table
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={exportLoading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
              onClick={handleExportToExcel}
              disabled={exportLoading || loading || rows.length === 0}
            >
              {exportLoading ? 'Exporting...' : 'Export to Excel'}
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={handleAddProvider}
            >
              Add Provider
            </Button>
          </Box>
        </Box>
        
        <Typography paragraph>
          Manage service provider rates for payment calculations. Each provider can have different rate configurations for each service type.
        </Typography>
        
        {activeServiceTypes.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Active Service Types:</strong> {activeServiceTypes.map(st => st.name).join(', ')}
            {' '}({activeServiceTypes.length} total)
          </Alert>
        )}

        {/* Tabs for switching between views */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="Provider List" />
            <Tab label="Service Type Comparison" />
          </Tabs>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Provider List View */}
            {currentTab === 0 && (
              <SortableTable
                rows={rows}
                headCells={headCells}
                defaultOrderBy="name"
                tableId="provider-rates-table"
                defaultRowsPerPage={25}
                renderRow={renderRow}
                tableProps={{ sx: { minWidth: 650 }, size: "small", "aria-label": "provider rate table" }}
                tableContainerProps={{ 
                  component: Paper, 
                  sx: { mt: 3, overflow: 'auto', maxWidth: '100%' } 
                }}
                emptyContent={
                  <Alert severity="info">No providers found. Add a provider to get started.</Alert>
                }
              />
            )}

            {/* Service Type Comparison View */}
            {currentTab === 1 && (
              <ServiceTypeComparisonView 
                providers={rows}
                activeServiceTypes={activeServiceTypes}
              />
            )}
          </>
        )}
        
        {/* Delete confirmation dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete {providerToDelete?.name}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
}

export default RateTable;
